import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  Checkbox,
  Button,
  InputAdornment,
  CircularProgress,
  IconButton,
  Divider,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { Plus, Check } from '@phosphor-icons/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import {
  getCollectionsByCompany,
  addSessionToCollection,
  removeSessionFromCollection,
  createCollection
} from '@/services/sessionCollectionService';
import { SessionCollection } from '@/types/sessionCollectionTypes';
import { Client } from '@langchain/langgraph-sdk';
import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const LANGGRAPH_BASE_URL =
  import.meta.env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';

/**
 * Helper function to acquire Auth0 access token
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn('Silent token acquisition failed:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error('Interactive token acquisition failed.');
      }
      return token;
    }
    throw error;
  }
};

interface MobileCollectionDrawerProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  currentCollections: SessionCollection[];
  onUpdate: () => void;
}

export const MobileCollectionDrawer: React.FC<MobileCollectionDrawerProps> = ({
  open,
  onClose,
  sessionId,
  currentCollections,
  onUpdate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();

  const [allCollections, setAllCollections] = useState<SessionCollection[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCollectionTitle, setNewCollectionTitle] = useState('');
  const [creating, setCreating] = useState(false);

  // Local optimistic state for current collections
  const [optimisticCollectionIds, setOptimisticCollectionIds] = useState<
    Set<string>
  >(new Set());

  // Sync optimistic state with props
  useEffect(() => {
    setOptimisticCollectionIds(new Set(currentCollections.map((c) => c.id)));
  }, [currentCollections]);

  // Use optimistic state for UI
  const currentCollectionIds = Array.from(optimisticCollectionIds);

  // Load all collections when drawer opens
  const loadCollections = useCallback(async () => {
    if (!selectedUserRole || !appUser) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getCollectionsByCompany(
        selectedUserRole.companyId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        1,
        100,
        searchTerm || undefined
      );
      setAllCollections(data);
    } catch (err) {
      console.error('Error loading collections:', err);
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, [
    selectedUserRole,
    appUser,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    searchTerm
  ]);

  useEffect(() => {
    if (open) {
      loadCollections();
    }
  }, [open, loadCollections]);

  // Debounce search
  useEffect(() => {
    if (!open) return;
    const timeout = setTimeout(() => {
      loadCollections();
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, open]);

  // Trigger collection summary agent (non-blocking background operation)
  const triggerCollectionSummary = useCallback(
    async (collectionId: string) => {
      if (!appUser) return;

      try {
        const token = await getAuthToken(
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        const client = new Client({
          apiUrl: LANGGRAPH_BASE_URL,
          defaultHeaders: {
            Authorization: `Bearer ${token}`,
            'X-User-Id': appUser.id
          }
        });

        // Create a new thread for this collection summary run
        const thread = await client.threads.create();

        console.log(
          'Triggering collection summary agent for collection:',
          collectionId
        );

        // Trigger the collection_summary_flow
        const stream = client.runs.stream(
          thread.thread_id,
          'collection_summary_flow',
          {
            input: {
              messages: [],
              collection_id: collectionId
            },
            config: {
              configurable: {
                auth_token: token,
                collection_id: collectionId,
                user_id: appUser.id
              }
            },
            metadata: {
              collection_id: collectionId,
              user_id: appUser.id,
              thread_id: thread.thread_id
            },
            streamMode: 'updates'
          }
        );

        // Run the stream consumption in the background (don't await)
        // The stream MUST be consumed for the agent to actually run
        (async () => {
          try {
            // Consume the stream
            for await (const _chunk of stream as any) {
              // Just consume the stream, don't need to do anything with chunks
            }
            console.log('Collection summary completed for:', collectionId);
          } catch (err) {
            console.error('Collection summary stream error:', err);
          }
        })();
      } catch (err) {
        console.error('Error triggering collection summary:', err);
        // Don't show error to user - this is a background operation
      }
    },
    [appUser, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const handleToggleCollection = async (collectionId: string) => {
    if (!appUser || updating) return;

    const isCurrentlyInCollection = currentCollectionIds.includes(collectionId);
    setUpdating(collectionId);
    setError(null);

    // Optimistically update UI immediately
    setOptimisticCollectionIds((prev) => {
      const newSet = new Set(prev);
      if (isCurrentlyInCollection) {
        newSet.delete(collectionId);
      } else {
        newSet.add(collectionId);
      }
      return newSet;
    });

    try {
      if (isCurrentlyInCollection) {
        await removeSessionFromCollection(
          collectionId,
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      } else {
        await addSessionToCollection(
          collectionId,
          sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }

      // Trigger parent update (will sync with server state)
      onUpdate();

      // Trigger collection summary agent in the background (non-blocking)
      triggerCollectionSummary(collectionId).catch((err) => {
        console.error('Error triggering collection summary:', err);
        // Don't show error to user - this is a background operation
      });
    } catch (err) {
      console.error('Error toggling collection:', err);
      setError('Failed to update collection');

      // Revert optimistic update on error
      setOptimisticCollectionIds((prev) => {
        const newSet = new Set(prev);
        if (isCurrentlyInCollection) {
          newSet.add(collectionId);
        } else {
          newSet.delete(collectionId);
        }
        return newSet;
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleCreateCollection = async () => {
    if (!appUser || !selectedUserRole || !newCollectionTitle.trim() || creating)
      return;

    setCreating(true);
    setError(null);

    try {
      // Create collection and automatically link this session to it
      const newCollection = await createCollection(
        {
          title: newCollectionTitle.trim(),
          description: '',
          referenceNumber: '',
          companyId: selectedUserRole.companyId,
          transcriptionSessionId: sessionId // Backend will automatically link the session
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      // Optimistically add to current collections immediately
      if (newCollection.id) {
        setOptimisticCollectionIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(newCollection.id);
          return newSet;
        });
      }

      // Reset and reload
      setNewCollectionTitle('');
      setShowCreateDialog(false);
      await loadCollections();
      onUpdate();

      // Trigger collection summary agent in the background (non-blocking)
      if (newCollection.id) {
        triggerCollectionSummary(newCollection.id).catch((err) => {
          console.error('Error triggering collection summary:', err);
          // Don't show error to user - this is a background operation
        });
      }
    } catch (err) {
      console.error('Error creating collection:', err);
      setError('Failed to create collection');
    } finally {
      setCreating(false);
    }
  };

  const filteredCollections = allCollections.filter((collection) =>
    searchTerm
      ? collection.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        collection.referenceNumber
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
      : true
  );

  // Close drawer when switching from mobile to desktop
  useEffect(() => {
    if (!isMobile && open) {
      onClose();
    }
  }, [isMobile, open, onClose]);

  if (!isMobile) return null;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      keepMounted={false}
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true
      }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          height: showCreateDialog ? '90vh' : '70vh',
          maxHeight: '90vh'
        }
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
              {showCreateDialog ? 'New Folder' : 'Add to Folder'}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ borderRadius: '6px', color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {showCreateDialog ? (
            /* Create New Collection Form */
            <>
              <TextField
                fullWidth
                placeholder="Collection name..."
                value={newCollectionTitle}
                onChange={(e) => setNewCollectionTitle(e.target.value)}
                autoFocus
                disabled={creating}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: '15px'
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newCollectionTitle.trim()) {
                    handleCreateCollection();
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setShowCreateDialog(false);
                    setNewCollectionTitle('');
                  }}
                  disabled={creating}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.25
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCreateCollection}
                  disabled={!newCollectionTitle.trim() || creating}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.25,
                    backgroundColor:
                      theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                    }
                  }}
                >
                  {creating ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Create'
                  )}
                </Button>
              </Box>
            </>
          ) : (
            /* Search Field */
            <TextField
              fullWidth
              placeholder="Search folders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '15px'
                }
              }}
            />
          )}
        </Box>

        {/* Error Alert */}
        {error && !showCreateDialog && (
          <Box sx={{ px: 2.5, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Collections List */}
        {!showCreateDialog && (
          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : filteredCollections.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                  {searchTerm ? 'No folders found' : 'No folders yet'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredCollections.map((collection) => {
                  const isChecked = currentCollectionIds.includes(collection.id);
                  const isUpdating = updating === collection.id;

                  return (
                    <ListItem
                      key={collection.id}
                      onClick={() =>
                        !isUpdating && handleToggleCollection(collection.id)
                      }
                      sx={{
                        borderRadius: '8px',
                        mb: 0.5,
                        px: 2,
                        py: 1.5,
                        cursor: isUpdating ? 'wait' : 'pointer',
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          backgroundColor: isUpdating
                            ? 'transparent'
                            : theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05)'
                            : '#f9fafb'
                        }
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: '15px',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {collection.title}
                        </Typography>
                        {collection.referenceNumber && (
                          <Typography
                            sx={{
                              fontSize: '13px',
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {collection.referenceNumber}
                          </Typography>
                        )}
                      </Box>
                      {isUpdating ? (
                        <CircularProgress size={20} sx={{ ml: 2 }} />
                      ) : (
                        <Checkbox
                          checked={isChecked}
                          sx={{ p: 0, ml: 2 }}
                          icon={
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                border: '2px solid',
                                borderColor: theme.palette.divider,
                                borderRadius: '4px'
                              }}
                            />
                          }
                          checkedIcon={
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? '#3b82f6'
                                    : '#1A1A1A',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Check size={14} weight="bold" color="white" />
                            </Box>
                          }
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        )}

        {/* Create New Button */}
        {!showCreateDialog && (
          <>
            <Divider />
            <Box sx={{ p: 2.5 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateDialog(true)}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f9fafb',
                    borderColor: theme.palette.divider
                  }
                }}
              >
                Create New Folder
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

